import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import LanguageSelector from '../common/LanguageSelector';

interface ObjectStorageSettings {
  enabled: boolean;
  provider: 's3' | 'custom';
  endpoint: string;
  bucket: string;
  accessKey: string;
  secretKey: string;
  cdnUrl?: string;
  region?: string;
}

interface PaymentGateway {
  momo: {
    enabled: boolean;
    partnerCode?: string;
    accessKey?: string;
    secretKey?: string;
    environment: 'development' | 'production';
    returnUrl?: string;
    notifyUrl?: string;
  };
  vnpay: {
    enabled: boolean;
    tmnCode?: string;
    hashSecret?: string;
    environment: 'development' | 'production';
  };
  zalopay: {
    enabled: boolean;
    appId?: string;
    key1?: string;
    key2?: string;
    environment: 'development' | 'production';
  };
}

interface MapsPlatform {
  google: {
    enabled: boolean;
    apiKey?: string;
  };
  mapbox: {
    enabled: boolean;
    apiKey?: string;
  };
}

interface SettingsForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  newEmail?: string;
}

interface PublicChargingSettings {
  allowUserAddLocation: boolean;
}

const AdminSettings: React.FC = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState<SettingsForm>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    newEmail: ''
  });
  const [paymentGateways, setPaymentGateways] = useState<PaymentGateway>({
    momo: {
      enabled: false,
      environment: 'development',
      returnUrl: '',
      notifyUrl: ''
    },
    vnpay: {
      enabled: false,
      environment: 'development'
    },
    zalopay: {
      enabled: false,
      environment: 'development'
    }
  });
  const [mapsPlatform, setMapsPlatform] = useState<MapsPlatform>({
    google: {
      enabled: false,
      apiKey: ''
    },
    mapbox: {
      enabled: true,
      apiKey: ''
    }
  });
  const [publicChargingSettings, setPublicChargingSettings] = useState<PublicChargingSettings>({
    allowUserAddLocation: false
  });
  const [loading, setLoading] = useState({
    password: false,
    email: false,
    gateways: false,
    maps: false,
    publicCharging: false,
    objectStorage: false,
    notifications: false
  });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [objectStorageSettings, setObjectStorageSettings] = useState<ObjectStorageSettings>({
    enabled: false,
    provider: 's3',
    endpoint: '',
    bucket: '',
    accessKey: '',
    secretKey: '',
    cdnUrl: '',
    region: ''
  });

  const [notificationSettings, setNotificationSettings] = useState({
    fcmServerKey: '',
    apnsKeyId: '',
    apnsTeamId: '',
    apnsBundleId: '',
    apnsProduction: false,
    firebaseServiceAccount: '',
  });

  useEffect(() => {
    fetchPaymentGateways();
    fetchMapsPlatform();
    fetchPublicChargingSettings();
    fetchObjectStorageSettings();
    fetchNotificationSettings();
  }, []);

  const fetchPaymentGateways = async () => {
    try {
      setLoading(prev => ({ ...prev, gateways: true }));
      const response = await api.get('/admin/payment-gateways');
      setPaymentGateways(response.data);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to fetch payment gateways');
    } finally {
      setLoading(prev => ({ ...prev, gateways: false }));
    }
  };

  const fetchMapsPlatform = async () => {
    try {
      setLoading(prev => ({ ...prev, maps: true }));
      const response = await api.get('/admin/maps-platform');
      setMapsPlatform(response.data);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to fetch maps platform settings');
    } finally {
      setLoading(prev => ({ ...prev, maps: false }));
    }
  };

  const fetchPublicChargingSettings = async () => {
    try {
      setLoading(prev => ({ ...prev, publicCharging: true }));
      const response = await api.get('/settings/public-charging');
      setPublicChargingSettings(response.data);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to fetch public charging settings');
    } finally {
      setLoading(prev => ({ ...prev, publicCharging: false }));
    }
  };

  const fetchObjectStorageSettings = async () => {
    try {
      setLoading(prev => ({ ...prev, objectStorage: true }));
      const response = await api.get('/settings/object-storage');
      setObjectStorageSettings(response.data);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to fetch object storage settings');
    } finally {
      setLoading(prev => ({ ...prev, objectStorage: false }));
    }
  };

  const fetchNotificationSettings = async () => {
    try {
      setLoading(prev => ({ ...prev, notifications: true }));
      const response = await api.get('/notifications/settings');
      setNotificationSettings(response.data);
    } catch (error: any) {
      console.error('Error fetching notification settings:', error);
      setError(error.response?.data?.message || 'Failed to fetch notification settings');
    } finally {
      setLoading(prev => ({ ...prev, notifications: false }));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
    setSuccessMessage('');
  };

  const handleGatewayChange = (gateway: 'momo' | 'vnpay' | 'zalopay', field: string, value: any) => {
    setPaymentGateways(prev => ({
      ...prev,
      [gateway]: {
        ...prev[gateway],
        [field]: value,
        // If disabling the gateway, reset all fields except environment
        ...(field === 'enabled' && !value ? {
          environment: prev[gateway].environment,
          enabled: false,
          ...(gateway === 'momo' && { returnUrl: '', notifyUrl: '' })
        } : {})
      }
    }));
    setError('');
    setSuccessMessage('');
  };

  const handleMapsPlatformChange = async (platform: 'google' | 'mapbox', field: string, value: any) => {
    if (field === 'enabled') {
      // If enabling one platform, disable the other
      setMapsPlatform(prev => ({
        google: {
          ...prev.google,
          enabled: platform === 'google' ? value : false
        },
        mapbox: {
          ...prev.mapbox,
          enabled: platform === 'mapbox' ? value : false
        }
      }));
    } else {
      setMapsPlatform(prev => ({
        ...prev,
        [platform]: {
          ...prev[platform],
          [field]: value
        }
      }));
    }
    setError('');
    setSuccessMessage('');
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.newPassword !== form.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    try {
      setLoading(prev => ({ ...prev, password: true }));
      await api.put('/auth/change-password', {
        currentPassword: form.currentPassword,
        newPassword: form.newPassword
      });
      
      setSuccessMessage('Password updated successfully');
      setForm(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to update password');
    } finally {
      setLoading(prev => ({ ...prev, password: false }));
    }
  };

  const handleEmailChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.newEmail) {
      setError('Please enter a new email');
      return;
    }

    try {
      setLoading(prev => ({ ...prev, email: true }));
      await api.put('/auth/change-email', {
        email: form.newEmail
      });
      
      setSuccessMessage('Email updated successfully');
      setForm(prev => ({
        ...prev,
        newEmail: ''
      }));
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to update email');
    } finally {
      setLoading(prev => ({ ...prev, email: false }));
    }
  };

  const handleGatewaySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(prev => ({ ...prev, gateways: true }));
      
      // Create a properly structured update object
      const updateData = {
        momo: {
          enabled: paymentGateways.momo.enabled,
          environment: paymentGateways.momo.environment || 'development',
          ...(paymentGateways.momo.enabled ? {
            partnerCode: paymentGateways.momo.partnerCode,
            accessKey: paymentGateways.momo.accessKey,
            secretKey: paymentGateways.momo.secretKey,
            returnUrl: paymentGateways.momo.returnUrl,
            notifyUrl: paymentGateways.momo.notifyUrl
          } : {})
        },
        vnpay: {
          enabled: paymentGateways.vnpay.enabled,
          environment: paymentGateways.vnpay.environment || 'development',
          ...(paymentGateways.vnpay.enabled ? {
            tmnCode: paymentGateways.vnpay.tmnCode,
            hashSecret: paymentGateways.vnpay.hashSecret
          } : {})
        },
        zalopay: {
          enabled: paymentGateways.zalopay.enabled,
          environment: paymentGateways.zalopay.environment || 'development',
          ...(paymentGateways.zalopay.enabled ? {
            appId: paymentGateways.zalopay.appId,
            key1: paymentGateways.zalopay.key1,
            key2: paymentGateways.zalopay.key2
          } : {})
        }
      };

      await api.put('/admin/payment-gateways', updateData);
      setSuccessMessage('Payment gateways updated successfully');
      
      // Refresh the payment gateways data
      await fetchPaymentGateways();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to update payment gateways');
    } finally {
      setLoading(prev => ({ ...prev, gateways: false }));
    }
  };

  const handleMapsPlatformSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(prev => ({ ...prev, maps: true }));
      await api.put('/admin/maps-platform', mapsPlatform);
      setSuccessMessage('Maps platform settings updated successfully');
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to update maps platform settings');
    } finally {
      setLoading(prev => ({ ...prev, maps: false }));
    }
  };

  const handlePublicChargingSettingsChange = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(prev => ({ ...prev, publicCharging: true }));
      await api.put('/settings/public-charging', publicChargingSettings);
      setSuccessMessage('Public charging settings updated successfully');
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to update public charging settings');
    } finally {
      setLoading(prev => ({ ...prev, publicCharging: false }));
    }
  };

  const handleObjectStorageChange = (field: keyof ObjectStorageSettings, value: any) => {
    setObjectStorageSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
    setSuccessMessage('');
  };

  const handleObjectStorageSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(prev => ({ ...prev, objectStorage: true }));
      setError('');
      setSuccessMessage('');
      
      console.log('Submitting object storage settings:', objectStorageSettings);
      
      // Create a clean settings object with only the fields we want to send
      const settingsToSave = {
        enabled: objectStorageSettings.enabled,
        provider: objectStorageSettings.provider,
        endpoint: objectStorageSettings.endpoint,
        bucket: objectStorageSettings.bucket,
        accessKey: objectStorageSettings.accessKey,
        secretKey: objectStorageSettings.secretKey,
        cdnUrl: objectStorageSettings.cdnUrl,
        region: objectStorageSettings.region
      };

      console.log('Saving settings:', settingsToSave);
      
      // First save the settings
      const response = await api.put('/settings/object-storage', settingsToSave);
      console.log('Save response:', response.data);
      
      // Then test the connection if enabled
      if (objectStorageSettings.enabled) {
        try {
          console.log('Testing connection...');
          const testResponse = await api.post('/settings/object-storage/test');
          console.log('Connection test successful:', testResponse.data);
          setSuccessMessage('Object storage settings updated and connection tested successfully');
        } catch (error: any) {
          console.error('Connection test failed:', error);
          // Handle specific error cases
          if (error.response?.status === 503) {
            setError('Network error - unable to reach the S3 endpoint. Please check your endpoint URL and network connection.');
          } else if (error.response?.status === 401) {
            setError('Invalid credentials - please check your access key and secret key.');
          } else if (error.response?.status === 404) {
            setError('Bucket not found - please check your bucket name.');
          } else {
            setError(error.response?.data?.message || 'Connection test failed. Please check the error message above.');
          }
          // Still show success message for saving settings
          setSuccessMessage('Settings saved but connection test failed. Please check the error message above.');
        }
      } else {
        setSuccessMessage('Object storage settings updated successfully');
      }
      
      // Refresh the settings
      await fetchObjectStorageSettings();
    } catch (error: any) {
      console.error('Error updating object storage settings:', error);
      setError(error.response?.data?.message || 'Failed to update object storage settings');
    } finally {
      setLoading(prev => ({ ...prev, objectStorage: false }));
    }
  };

  const handleNotificationSettingChange = (field: string, value: string | boolean) => {
    setNotificationSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
    setSuccessMessage('');
  };

  const handleNotificationSettingsSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setLoading(prev => ({ ...prev, notifications: true }));
      setError('');
      setSuccessMessage('');
      
      const response = await api.put('/notifications/settings', notificationSettings);
      
      setSuccessMessage('Notification settings updated successfully');
    } catch (error: any) {
      console.error('Error updating notification settings:', error);
      setError(error.response?.data?.message || 'Failed to update notification settings');
    } finally {
      setLoading(prev => ({ ...prev, notifications: false }));
    }
  };

  const handleTestNotification = async () => {
    try {
      setLoading(prev => ({ ...prev, notifications: true }));
      setError('');
      setSuccessMessage('');
      
      let pushToken = null;
      let tokenType = 'fcm';
      
      try {
        // Try to get current user info
        const currentUser = await api.get('/auth/me');
        pushToken = currentUser.data.pushToken;
        tokenType = currentUser.data.pushTokenType || 'fcm';
      } catch (error) {
        console.error('Error fetching current user:', error);
        // Continue with manual entry or show error
      }
      
      if (!pushToken) {
        // If no token is found or there was an error, use a test token
        const testTokenResponse = window.prompt(
          'Enter a valid push token for testing:\n\n' +
          '- For FCM (Android): should be a long string starting with "f" or "d"\n' +
          '- For APNs (iOS): should be a device token hex string\n' +
          '- For Expo: should start with "ExponentPushToken"'
        );
        
        if (!testTokenResponse) {
          setError('No push token provided for testing.');
          setLoading(prev => ({ ...prev, notifications: false }));
          return;
        }
        
        if (testTokenResponse.length < 10) {
          setError('The token provided is too short to be valid. Please enter a real device token.');
          setLoading(prev => ({ ...prev, notifications: false }));
          return;
        }
        
        pushToken = testTokenResponse;
        
        // Try to auto-detect token type
        if (testTokenResponse.startsWith('ExponentPushToken')) {
          tokenType = 'expo';
        } else if (testTokenResponse.length > 140) {
          // APNs tokens are typically longer
          tokenType = 'apns';
        } else {
          tokenType = 'fcm';
        }
        
        const selectedTokenType = window.prompt(
          `Select token type (detected as ${tokenType}):\n` +
          '1. FCM (Android)\n' +
          '2. APNs (iOS)\n' +
          '3. Expo',
          tokenType === 'fcm' ? '1' : tokenType === 'apns' ? '2' : '3'
        );
        
        if (selectedTokenType === '1') tokenType = 'fcm';
        else if (selectedTokenType === '2') tokenType = 'apns';
        else if (selectedTokenType === '3') tokenType = 'expo';
      }
      
      console.log(`Sending test notification with token: ${pushToken?.substring(0, 10)}... (${tokenType})`);
      
      // Send a test notification
      const response = await api.post('/notifications/settings/test', {
        token: pushToken,
        tokenType: tokenType
      });
      
      setSuccessMessage('Test notification sent successfully! Check your device.');
    } catch (error: any) {
      console.error('Error sending test notification:', error);
      let errorMessage = 'Failed to send test notification';
      
      // Extract more detailed error messages from the response if available
      if (error.response?.data) {
        if (error.response.data.details) {
          errorMessage = error.response.data.details;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Common FCM errors
      if (errorMessage.includes('404') || errorMessage.includes('FCM')) {
        errorMessage = `${errorMessage}\n\nTo fix this issue, try:\n1. Check your Firebase Server Key in the Notification Settings\n2. Make sure you're using a valid device token`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(prev => ({ ...prev, notifications: false }));
    }
  };

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold mb-6">{t('settings.adminSettings')}</h2>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {successMessage}
          </div>
        )}

        {/* Language Settings */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4">{t('settings.language')}</h3>
          <div className="max-w-md">
            <LanguageSelector />
          </div>
        </div>

        {/* Maps Platform Section */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4">{t('settings.mapsPlatform.title')}</h3>
          <p className="text-gray-600 mb-4">{t('settings.mapsPlatform.description')}</p>
          
          <form onSubmit={handleMapsPlatformSubmit} className="space-y-6">
            {/* Google Maps */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-md font-medium">{t('settings.mapsPlatform.google.title')}</h4>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={mapsPlatform.google.enabled}
                    onChange={(e) => handleMapsPlatformChange('google', 'enabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
              
              {mapsPlatform.google.enabled && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.mapsPlatform.google.apiKey')}
                  </label>
                  <input
                    type="password"
                    value={mapsPlatform.google.apiKey || ''}
                    onChange={(e) => handleMapsPlatformChange('google', 'apiKey', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required={mapsPlatform.google.enabled}
                  />
                </div>
              )}
            </div>

            {/* Mapbox */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-md font-medium">{t('settings.mapsPlatform.mapbox.title')}</h4>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={mapsPlatform.mapbox.enabled}
                    onChange={(e) => handleMapsPlatformChange('mapbox', 'enabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
              
              {mapsPlatform.mapbox.enabled && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.mapsPlatform.mapbox.apiKey')}
                  </label>
                  <input
                    type="password"
                    value={mapsPlatform.mapbox.apiKey || ''}
                    onChange={(e) => handleMapsPlatformChange('mapbox', 'apiKey', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required={mapsPlatform.mapbox.enabled}
                  />
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={loading.maps}
              className={`w-full bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                loading.maps ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {loading.maps ? t('common.loading') : t('common.save')}
            </button>
          </form>
        </div>

        {/* Payment Gateways Section */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4">{t('settings.paymentGateways')}</h3>
          <form onSubmit={handleGatewaySubmit} className="space-y-6">
            {/* Momo Gateway */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-md font-medium">{t('payment.momo')}</h4>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={paymentGateways.momo.enabled}
                    onChange={(e) => handleGatewayChange('momo', 'enabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
              
              {paymentGateways.momo.enabled && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('payment.partnerCode')}
                    </label>
                    <input
                      type="text"
                      value={paymentGateways.momo.partnerCode || ''}
                      onChange={(e) => handleGatewayChange('momo', 'partnerCode', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required={paymentGateways.momo.enabled}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Access Key
                    </label>
                    <input
                      type="text"
                      value={paymentGateways.momo.accessKey || ''}
                      onChange={(e) => handleGatewayChange('momo', 'accessKey', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required={paymentGateways.momo.enabled}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Secret Key
                    </label>
                    <input
                      type="password"
                      value={paymentGateways.momo.secretKey || ''}
                      onChange={(e) => handleGatewayChange('momo', 'secretKey', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required={paymentGateways.momo.enabled}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Return URL
                    </label>
                    <input
                      type="url"
                      value={paymentGateways.momo.returnUrl || ''}
                      onChange={(e) => handleGatewayChange('momo', 'returnUrl', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="evchargingapp://payment-methods"
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      The URL scheme that will open your app after MoMo binding (e.g., evchargingapp://payment-methods)
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notify URL (IPN)
                    </label>
                    <input
                      type="url"
                      value={paymentGateways.momo.notifyUrl || ''}
                      onChange={(e) => handleGatewayChange('momo', 'notifyUrl', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="https://your-api.com/api/wallet/momo/ipn"
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Your backend API endpoint that will receive MoMo notifications
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Environment
                    </label>
                    <select
                      value={paymentGateways.momo.environment}
                      onChange={(e) => handleGatewayChange('momo', 'environment', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="development">Development</option>
                      <option value="production">Production</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* VNPay Gateway */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-md font-medium">{t('payment.vnpay')}</h4>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={paymentGateways.vnpay.enabled}
                    onChange={(e) => handleGatewayChange('vnpay', 'enabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
              
              {paymentGateways.vnpay.enabled && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      TMN Code
                    </label>
                    <input
                      type="text"
                      value={paymentGateways.vnpay.tmnCode || ''}
                      onChange={(e) => handleGatewayChange('vnpay', 'tmnCode', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required={paymentGateways.vnpay.enabled}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Hash Secret
                    </label>
                    <input
                      type="password"
                      value={paymentGateways.vnpay.hashSecret || ''}
                      onChange={(e) => handleGatewayChange('vnpay', 'hashSecret', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required={paymentGateways.vnpay.enabled}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Environment
                    </label>
                    <select
                      value={paymentGateways.vnpay.environment}
                      onChange={(e) => handleGatewayChange('vnpay', 'environment', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="development">Development</option>
                      <option value="production">Production</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* ZaloPay Gateway */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-md font-medium">{t('payment.zalopay')}</h4>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={paymentGateways.zalopay.enabled}
                    onChange={(e) => handleGatewayChange('zalopay', 'enabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
              
              {paymentGateways.zalopay.enabled && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      App ID
                    </label>
                    <input
                      type="text"
                      value={paymentGateways.zalopay.appId || ''}
                      onChange={(e) => handleGatewayChange('zalopay', 'appId', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required={paymentGateways.zalopay.enabled}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Key 1
                    </label>
                    <input
                      type="password"
                      value={paymentGateways.zalopay.key1 || ''}
                      onChange={(e) => handleGatewayChange('zalopay', 'key1', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required={paymentGateways.zalopay.enabled}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Key 2
                    </label>
                    <input
                      type="password"
                      value={paymentGateways.zalopay.key2 || ''}
                      onChange={(e) => handleGatewayChange('zalopay', 'key2', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required={paymentGateways.zalopay.enabled}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Environment
                    </label>
                    <select
                      value={paymentGateways.zalopay.environment}
                      onChange={(e) => handleGatewayChange('zalopay', 'environment', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="development">Development</option>
                      <option value="production">Production</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={loading.gateways}
              className={`w-full bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                loading.gateways ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {loading.gateways ? t('common.loading') : t('common.save')}
            </button>
          </form>
        </div>

        {/* Public Charging Settings Section */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4">{t('settings.publicCharging.title')}</h3>
          <form onSubmit={handlePublicChargingSettingsChange} className="space-y-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="text-md font-medium">{t('settings.publicCharging.allowUserAddLocation')}</h4>
                  <p className="text-sm text-gray-600 mt-1">{t('settings.publicCharging.allowUserAddLocationDescription')}</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={publicChargingSettings.allowUserAddLocation}
                    onChange={(e) => setPublicChargingSettings(prev => ({
                      ...prev,
                      allowUserAddLocation: e.target.checked
                    }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading.publicCharging}
              className={`w-full bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                loading.publicCharging ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {loading.publicCharging ? t('common.loading') : t('common.save')}
            </button>
          </form>
        </div>

        {/* Change Password Form */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4">{t('auth.password')}</h3>
          <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('auth.currentPassword')}
              </label>
              <input
                type="password"
                name="currentPassword"
                value={form.currentPassword}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                required
                placeholder={t('auth.currentPassword')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('auth.newPassword')}
              </label>
              <input
                type="password"
                name="newPassword"
                value={form.newPassword}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                required
                placeholder={t('auth.newPassword')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('auth.confirmPassword')}
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={form.confirmPassword}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                required
                placeholder={t('auth.confirmPassword')}
              />
            </div>
            <button
              type="submit"
              disabled={loading.password}
              className={`w-full bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                loading.password ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {loading.password ? t('common.loading') : t('common.save')}
            </button>
          </form>
        </div>

        {/* Change Email Form */}
        <div className="border-t pt-8">
          <h3 className="text-lg font-semibold mb-4">{t('auth.email')}</h3>
          <form onSubmit={handleEmailChange} className="space-y-4 max-w-md">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('auth.email')}
              </label>
              <input
                type="email"
                name="newEmail"
                value={form.newEmail}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                required
                placeholder={t('auth.email')}
              />
            </div>
            <button
              type="submit"
              disabled={loading.email}
              className={`w-full bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                loading.email ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {loading.email ? t('common.loading') : t('common.save')}
            </button>
          </form>
        </div>

        {/* Object Storage Settings Section */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4">{t('settings.objectStorage.title')}</h3>
          <form onSubmit={handleObjectStorageSubmit} className="space-y-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="text-md font-medium">{t('settings.objectStorage.enabled')}</h4>
                  <p className="text-sm text-gray-600 mt-1">{t('settings.objectStorage.enabledDescription')}</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={objectStorageSettings.enabled}
                    onChange={(e) => handleObjectStorageChange('enabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>

            {objectStorageSettings.enabled && (
              <>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="text-md font-medium">{t('settings.objectStorage.endpoint')}</h4>
                      <p className="text-sm text-gray-600 mt-1">{t('settings.objectStorage.endpointDescription')}</p>
                    </div>
                    <input
                      type="text"
                      value={objectStorageSettings.endpoint}
                      onChange={(e) => handleObjectStorageChange('endpoint', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="https://s3.amazonaws.com"
                    />
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="text-md font-medium">{t('settings.objectStorage.bucket')}</h4>
                      <p className="text-sm text-gray-600 mt-1">{t('settings.objectStorage.bucketDescription')}</p>
                    </div>
                    <input
                      type="text"
                      value={objectStorageSettings.bucket}
                      onChange={(e) => handleObjectStorageChange('bucket', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="my-bucket-name"
                    />
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="text-md font-medium">{t('settings.objectStorage.accessKey')}</h4>
                      <p className="text-sm text-gray-600 mt-1">{t('settings.objectStorage.accessKeyDescription')}</p>
                    </div>
                    <input
                      type="text"
                      value={objectStorageSettings.accessKey}
                      onChange={(e) => handleObjectStorageChange('accessKey', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="AKIAXXXXXXXXXXXXXXXX"
                    />
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="text-md font-medium">{t('settings.objectStorage.secretKey')}</h4>
                      <p className="text-sm text-gray-600 mt-1">{t('settings.objectStorage.secretKeyDescription')}</p>
                    </div>
                    <input
                      type="password"
                      value={objectStorageSettings.secretKey}
                      onChange={(e) => handleObjectStorageChange('secretKey', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="text-md font-medium">{t('settings.objectStorage.cdnUrl')}</h4>
                      <p className="text-sm text-gray-600 mt-1">{t('settings.objectStorage.cdnUrlDescription')}</p>
                    </div>
                    <input
                      type="text"
                      value={objectStorageSettings.cdnUrl || ''}
                      onChange={(e) => handleObjectStorageChange('cdnUrl', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="https://cdn.example.com"
                    />
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="text-md font-medium">{t('settings.objectStorage.region')}</h4>
                      <p className="text-sm text-gray-600 mt-1">{t('settings.objectStorage.regionDescription')}</p>
                    </div>
                    <input
                      type="text"
                      value={objectStorageSettings.region || ''}
                      onChange={(e) => handleObjectStorageChange('region', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="us-east-1"
                    />
                  </div>
                </div>
              </>
            )}

            <button
              type="submit"
              disabled={loading.objectStorage}
              className={`w-full bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                loading.objectStorage ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {loading.objectStorage ? t('common.loading') : t('common.save')}
            </button>
          </form>
        </div>

        {/* Notification Settings Section */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4">{t('settings.notifications.title')}</h3>
          <form onSubmit={handleNotificationSettingsSubmit} className="space-y-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="mb-6">
                <h4 className="text-md font-medium mb-2">{t('settings.notifications.firebase')}</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Firebase Cloud Messaging (FCM) now uses the V1 API which requires a service account. 
                  The legacy HTTP API using server keys is being deprecated.
                </p>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Firebase Service Account (JSON)
                  </label>
                  <textarea
                    placeholder="Paste your Firebase service account JSON here"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                    rows={6}
                    value={notificationSettings.firebaseServiceAccount || ''}
                    onChange={(e) => handleNotificationSettingChange('firebaseServiceAccount', e.target.value)}
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    From Firebase Console → Project settings → Service accounts → Generate new private key
                  </p>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Legacy FCM Server Key (Optional)
                  </label>
                  <input
                    type="password"
                    value={notificationSettings.fcmServerKey || ''}
                    onChange={(e) => handleNotificationSettingChange('fcmServerKey', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t('settings.notifications.fcmServerKeyPlaceholder')}
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Legacy server key (will be deprecated by Firebase)
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="text-md font-medium mb-2">{t('settings.notifications.apple')}</h4>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.notifications.apnsKeyId')}
                  </label>
                  <input
                    type="text"
                    value={notificationSettings.apnsKeyId || ''}
                    onChange={(e) => handleNotificationSettingChange('apnsKeyId', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t('settings.notifications.apnsKeyIdPlaceholder')}
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.notifications.apnsTeamId')}
                  </label>
                  <input
                    type="text"
                    value={notificationSettings.apnsTeamId || ''}
                    onChange={(e) => handleNotificationSettingChange('apnsTeamId', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t('settings.notifications.apnsTeamIdPlaceholder')}
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.notifications.apnsBundleId')}
                  </label>
                  <input
                    type="text"
                    value={notificationSettings.apnsBundleId || ''}
                    onChange={(e) => handleNotificationSettingChange('apnsBundleId', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="com.example.app"
                  />
                </div>

                <div className="flex items-center mb-4">
                  <input
                    id="apnsProduction"
                    type="checkbox"
                    checked={notificationSettings.apnsProduction || false}
                    onChange={(e) => handleNotificationSettingChange('apnsProduction', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="apnsProduction" className="ml-2 block text-sm text-gray-900">
                    {t('settings.notifications.apnsProduction')}
                  </label>
                </div>
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                type="submit"
                disabled={loading.notifications}
                className={`bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  loading.notifications ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {loading.notifications ? t('common.loading') : t('common.save')}
              </button>
              
              <button
                type="button"
                onClick={handleTestNotification}
                disabled={loading.notifications}
                className={`bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
                  loading.notifications ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {t('settings.notifications.testNotification')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings; 